/**
 * AnimatedSidebarIcons - Icônes SVG animées pour la barre latérale
 *
 * Inspirées des icônes de l'onboarding (Step1_ValuePropositionV3)
 * Utilise Framer Motion pour les animations "draw" élégantes
 */

import React from 'react';
import { motion } from 'framer-motion';

interface IconProps {
  color?: string;
  size?: number;
  isActive?: boolean;
}

// Animation variants communes
const drawVariants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (delay: number) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.6, delay, ease: 'easeInOut' },
      opacity: { duration: 0.2, delay },
    },
  }),
};

const scaleVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: (delay: number) => ({
    scale: 1,
    opacity: 1,
    transition: { duration: 0.3, delay, type: 'spring', stiffness: 300 },
  }),
};

// ============================================================================
// HOME ICON - Maison avec cheminée
// ============================================================================
export const HomeIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M3 9.5L12 3L21 9.5V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9.5Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M9 22V12H15V22"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.2}
    />
    {isActive && (
      <motion.circle
        cx="12"
        cy="7"
        r="1.5"
        fill={color}
        variants={scaleVariants}
        initial="hidden"
        animate="visible"
        custom={0.4}
      />
    )}
  </svg>
);

// ============================================================================
// CV BUILDER ICON - Document avec lignes
// ============================================================================
export const CVBuilderIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M14 2V8H20"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.15}
    />
    <motion.path
      d="M8 13H16"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.3}
    />
    <motion.path
      d="M8 17H13"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.4}
    />
    {isActive && (
      <motion.circle
        cx="8"
        cy="9"
        r="1.5"
        fill={color}
        variants={scaleVariants}
        initial="hidden"
        animate="visible"
        custom={0.5}
      />
    )}
  </svg>
);

// ============================================================================
// JOB SEARCH ICON - Loupe avec briefcase
// ============================================================================
export const JobSearchIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.circle
      cx="10"
      cy="10"
      r="7"
      stroke={color}
      strokeWidth="2"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M21 21L15.5 15.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.2}
    />
    <motion.rect
      x="7"
      y="8"
      width="6"
      height="4"
      rx="1"
      stroke={color}
      strokeWidth="1.5"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.35}
    />
    {isActive && (
      <motion.path
        d="M9 8V7C9 6.5 9.5 6 10 6C10.5 6 11 6.5 11 7V8"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        variants={drawVariants}
        initial="hidden"
        animate="visible"
        custom={0.45}
      />
    )}
  </svg>
);

// ============================================================================
// APPLICATIONS ICON - Dossiers empilés
// ============================================================================
export const ApplicationsIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    {isActive && (
      <>
        <motion.path
          d="M8 13H16"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          variants={drawVariants}
          initial="hidden"
          animate="visible"
          custom={0.3}
        />
        <motion.path
          d="M8 17H12"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          variants={drawVariants}
          initial="hidden"
          animate="visible"
          custom={0.4}
        />
      </>
    )}
  </svg>
);

// ============================================================================
// COACHING ICON - Chapeau de graduation avec étoile
// ============================================================================
export const CoachingIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M22 10L12 5L2 10L12 15L22 10Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M6 12V17C6 17 8 20 12 20C16 20 18 17 18 17V12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.2}
    />
    <motion.path
      d="M22 10V16"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.35}
    />
    {isActive && (
      <motion.circle
        cx="12"
        cy="10"
        r="1.5"
        fill={color}
        variants={scaleVariants}
        initial="hidden"
        animate="visible"
        custom={0.45}
      />
    )}
  </svg>
);

// ============================================================================
// PROFILE ICON - Utilisateur avec cercle
// ============================================================================
export const ProfileIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.circle
      cx="12"
      cy="8"
      r="4"
      stroke={color}
      strokeWidth="2"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M4 21V19C4 17.9391 4.42143 16.9217 5.17157 16.1716C5.92172 15.4214 6.93913 15 8 15H16C17.0609 15 18.0783 15.4214 18.8284 16.1716C19.5786 16.9217 20 17.9391 20 19V21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.2}
    />
    {isActive && (
      <motion.circle
        cx="12"
        cy="8"
        r="1.5"
        fill={color}
        variants={scaleVariants}
        initial="hidden"
        animate="visible"
        custom={0.4}
      />
    )}
  </svg>
);

// ============================================================================
// ADMIN ICON - Engrenage avec shield
// ============================================================================
export const AdminIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.circle
      cx="12"
      cy="11"
      r="3"
      stroke={color}
      strokeWidth="2"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.25}
    />
    {isActive && (
      <motion.path
        d="M12 8V9M12 13V14M9.5 9.5L10.2 10.2M13.8 11.8L14.5 12.5M9 11H10M14 11H15M9.5 12.5L10.2 11.8M13.8 10.2L14.5 9.5"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        variants={drawVariants}
        initial="hidden"
        animate="visible"
        custom={0.4}
      />
    )}
  </svg>
);

// ============================================================================
// COACH DASHBOARD ICON - Personnes avec tableau
// ============================================================================
export const CoachDashboardIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.circle
      cx="9"
      cy="7"
      r="3"
      stroke={color}
      strokeWidth="2"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.circle
      cx="17"
      cy="7"
      r="3"
      stroke={color}
      strokeWidth="2"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.1}
    />
    <motion.path
      d="M3 21V19C3 17.3431 4.34315 16 6 16H12C13.6569 16 15 17.3431 15 19V21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.25}
    />
    <motion.path
      d="M17 16C18.6569 16 20 17.3431 20 19V21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.35}
    />
    {isActive && (
      <motion.path
        d="M9 13V14M17 13V14"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        variants={drawVariants}
        initial="hidden"
        animate="visible"
        custom={0.45}
      />
    )}
  </svg>
);

// ============================================================================
// ORP DASHBOARD ICON - Bâtiment avec personnes (Office Régional de Placement)
// ============================================================================
export const ORPDashboardIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24, isActive }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M3 21H21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M5 21V7L12 3L19 7V21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.1}
    />
    <motion.rect
      x="9"
      y="10"
      width="6"
      height="6"
      rx="1"
      stroke={color}
      strokeWidth="1.5"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.25}
    />
    <motion.path
      d="M12 10V16"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.35}
    />
    <motion.path
      d="M9 13H15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.4}
    />
    {isActive && (
      <motion.circle
        cx="12"
        cy="6.5"
        r="1.5"
        fill={color}
        variants={scaleVariants}
        initial="hidden"
        animate="visible"
        custom={0.5}
      />
    )}
  </svg>
);

// ============================================================================
// LOGOUT ICON - Porte avec flèche
// ============================================================================
export const LogoutIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M16 17L21 12L16 7"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.15}
    />
    <motion.path
      d="M21 12H9"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.3}
    />
  </svg>
);

// ============================================================================
// REFERRAL (Gift) ICON
// ============================================================================
export const ReferralIcon: React.FC<IconProps> = ({ color = 'currentColor', size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <motion.path
      d="M20 12V22H4V12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0}
    />
    <motion.path
      d="M22 7H2V12H22V7Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.15}
    />
    <motion.path
      d="M12 22V7"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.3}
    />
    <motion.path
      d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.45}
    />
    <motion.path
      d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={drawVariants}
      initial="hidden"
      animate="visible"
      custom={0.6}
    />
  </svg>
);

// ============================================================================
// ICON GETTER
// ============================================================================
export type SidebarIconType =
  | 'home'
  | 'cv-builder'
  | 'job-search'
  | 'applications'
  | 'coaching'
  | 'referral'
  | 'profile'
  | 'admin'
  | 'coach-dashboard'
  | 'orp-dashboard'
  | 'logout';

export const getSidebarIcon = (
  type: SidebarIconType,
  props: IconProps = {}
): React.ReactNode => {
  const iconMap: Record<SidebarIconType, React.FC<IconProps>> = {
    home: HomeIcon,
    'cv-builder': CVBuilderIcon,
    'job-search': JobSearchIcon,
    applications: ApplicationsIcon,
    coaching: CoachingIcon,
    referral: ReferralIcon,
    profile: ProfileIcon,
    admin: AdminIcon,
    'coach-dashboard': CoachDashboardIcon,
    'orp-dashboard': ORPDashboardIcon,
    logout: LogoutIcon,
  };

  const IconComponent = iconMap[type];
  if (!IconComponent) return null;

  return <IconComponent {...props} />;
};

export default getSidebarIcon;
